/**
 * Copyright (c) 2026, CheckPlus Limited
 */
import React from "react";
import { TwitterPicker, HuePicker } from "react-color";
import { simpleFetch } from "../../../restWrapper";
import { resolveThemeColors, getContrastTextColor, getTextColor, sanitizePresetAmounts } from "../../../assets/helper";
import "../../MissionDetails/MissionDetails.css";

const DEFAULT_DONATION_PRESETS = [100, 250, 500, 1000];
const MIN_PRESET_AMOUNTS = 1;
const MAX_PRESET_AMOUNTS = 6;

export function RenderPaymentPageCustomization(state, func, textObj) {
  if (!state || !func) return;

  const customPresets = Array.isArray(state.organizationObj.presetDonationAmounts) &&
    state.organizationObj.presetDonationAmounts.length > 0
    ? state.organizationObj.presetDonationAmounts
    : null;

  const donationPreviewAmounts = customPresets || DEFAULT_DONATION_PRESETS;
  const editorAmounts = customPresets || DEFAULT_DONATION_PRESETS;

  function handlePresetAmountChange(index, e) {
    const raw = e.target.value;
    const nextPresets = [...editorAmounts];
    nextPresets[index] = raw === "" ? "" : Number(raw);
    func.setState({
      presetValidationError: null,
      organizationObj: {
        ...state.organizationObj,
        presetDonationAmounts: nextPresets,
      },
    });
  }

  function handleAddPresetAmount() {
    if (editorAmounts.length >= MAX_PRESET_AMOUNTS) return;
    func.setState({
      organizationObj: {
        ...state.organizationObj,
        presetDonationAmounts: [...editorAmounts, ""],
      },
    });
  }

  function handleRemovePresetAmount(index) {
    if (editorAmounts.length <= MIN_PRESET_AMOUNTS) return;
    const nextPresets = editorAmounts.filter((_, i) => i !== index);
    func.setState({
      organizationObj: {
        ...state.organizationObj,
        presetDonationAmounts: nextPresets,
      },
    });
  }

  function handleResetPresets() {
    func.setState({
      presetValidationError: null,
      organizationObj: {
        ...state.organizationObj,
        presetDonationAmounts: [],
      },
    });
  }

  function sanitizeHex(raw) {
    return raw.replace(/[^0-9A-Fa-f]/g, "").slice(0, 6);
  }

  function handleColorHexInput(handler, e) {
    handler({ hex: "#" + sanitizeHex(e.target.value) });
  }

  function toggleColorKey(fieldKey) {
    func.setState({ openColorPickerKey: state.openColorPickerKey === fieldKey ? null : fieldKey });
  }

  function startEditingPresetIndex(index) {
    func.setState({ editingPresetAmountIndex: index });
  }

  function stopEditingPresetIndex() {
    func.setState({ editingPresetAmountIndex: null });
  }

  function toggleColorsExpanded() {
    func.setState({ paymentColorsExpanded: !state.paymentColorsExpanded });
  }

  function toggleAmountsExpanded() {
    func.setState({ paymentAmountsExpanded: !state.paymentAmountsExpanded });
  }

  function clickSubmit(e) {
    // Never persist an invalid preset (CP-238): an empty or below-minimum
    // amount renders a donor button that fails validation silently — the
    // Donate button just dims with no error shown to anyone.
    const rawPresets = Array.isArray(state.organizationObj.presetDonationAmounts)
      ? state.organizationObj.presetDonationAmounts
      : [];
    const minimum = Number(state.organizationObj.minimumPaymentAmount) || 0;
    const enteredPresets = rawPresets.filter(
      (a) => a !== "" && a !== null && a !== undefined
    );
    const validPresets = sanitizePresetAmounts(rawPresets, minimum);
    if (validPresets.length !== enteredPresets.length) {
      const message =
        minimum > 0
          ? (textObj.presetBelowMinError ||
              "Each preset amount must be a number of at least {min}."
          ).replace("{min}", String(minimum))
          : textObj.presetInvalidError ||
            "Each preset amount must be a number greater than 0.";
      func.setState({ presetValidationError: message });
      return;
    }
    func.setState({ presetValidationError: null });

    let payload = {
      headerColor: state.organizationObj.headerColor,
      buttonColor: state.organizationObj.buttonColor,
      progressBarColor: state.organizationObj.progressBarColor,
      presetDonationAmounts: validPresets,
    };
    func.setState({
      loading: true,
    });
    simpleFetch("/organizations", "PUT", payload).then((res) => {
      func.setState({
        loading: false,
      });
      switch (res.status) {
        case 200:
        case 204:
          break;
        default:
          // console.error("Failed to save Payment Page Customization:", res.status, res.body);
          alert(textObj.saveError || "Failed to save changes. Please try again.");
          break;
      }
    });
  }

  if (state.loading) {
    return (
      <div className="PaymentPageCustomization-emptyPage">
        <div className="PaymentPageCustomization-center">
          <div className="PaymentPageCustomization-btm-load"></div>
        </div>
      </div>
    );
  }

  const resolved = resolveThemeColors(
    state.organizationObj.headerColor,
    state.organizationObj.buttonColor
  );
  const headerStyle = { background: "#" + resolved.headerColor };
  const buttonTextStyle = { color: getContrastTextColor(resolved.headerColor, resolved.buttonColor) };
  const headerTextStyle = { color: getTextColor(resolved.headerColor) };
  const logo = state.organizationObj.logo;

  function renderColorField(fieldKey, label, colorValue, handler) {
    const isOpen = state.openColorPickerKey === fieldKey;

    function toggleOpen() {
      func.setState({ openColorPickerKey: isOpen ? null : fieldKey });
    }

    function openOnInputClick(e) {
      e.stopPropagation();
      func.setState({ openColorPickerKey: fieldKey });
    }

    return (
      <div className="PaymentPageCustomization-colorField">
        <div className="PaymentPageCustomization-colorFieldRow" onClick={toggleOpen}>
          <div
            className="PaymentPageCustomization-colorSwatch"
            style={{ background: "#" + colorValue }}
          ></div>
          <span className="PaymentPageCustomization-colorFieldLabel">{label}</span>
          <div className="PaymentPageCustomization-colorHexWrap">
            <span className="PaymentPageCustomization-colorHexPrefix">#</span>
            <input
              type="text"
              className="PaymentPageCustomization-colorHexInput"
              value={colorValue}
              maxLength={6}
              onClick={openOnInputClick}
              onChange={(e) => handleColorHexInput(handler, e)}
            />
          </div>
          <div className={`PaymentPageCustomization-sectionChevron PaymentPageCustomization-colorFieldChevron ${isOpen ? "expanded" : ""}`}></div>
        </div>
        {isOpen && (
          <div className="PaymentPageCustomization-colorPickers">
            <HuePicker width={350} color={colorValue} onChangeComplete={handler} />
            <TwitterPicker width={350} color={colorValue} onChangeComplete={handler} triangle="hide" />
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="PaymentPageCustomization-content">
      <div className="PaymentPageCustomization-colorSelector">
        <div className="PaymentPageCustomization-contentHeader">
          <div className="PaymentPageCustomization-info">{textObj.info}</div>
          <div className="PaymentPageCustomization-submitBtn" onClick={clickSubmit}>
            {textObj.submitBtn}
          </div>
        </div>

        {state.presetValidationError && (
          <div className="PaymentPageCustomization-presetError">
            {state.presetValidationError}
          </div>
        )}

        <div
          className={`PaymentPageCustomization-sectionHeader ${state.paymentColorsExpanded ? "expanded" : ""}`}
          onClick={toggleColorsExpanded}
        >
          <span>{textObj.colorsHeading}</span>
          <div className={`PaymentPageCustomization-sectionChevron ${state.paymentColorsExpanded ? "expanded" : ""}`}></div>
        </div>

        {state.paymentColorsExpanded && (
          <div className="PaymentPageCustomization-sectionBody">
            {renderColorField("headerColor", textObj.inputColorArea[0], state.organizationObj.headerColor, func.handleOrgHeaderColor)}
            {renderColorField("progressBarColor", textObj.inputColorArea[1], state.organizationObj.progressBarColor, func.handleOrgBarColor)}
            {renderColorField("buttonColor", textObj.inputColorArea[2], state.organizationObj.buttonColor, func.handleOrgButtonColor)}
          </div>
        )}

        <div
          className={`PaymentPageCustomization-sectionHeader ${state.paymentAmountsExpanded ? "expanded" : ""}`}
          onClick={toggleAmountsExpanded}
        >
          <span>{textObj.presetAmountsHeading}</span>
          <div className={`PaymentPageCustomization-sectionChevron ${state.paymentAmountsExpanded ? "expanded" : ""}`}></div>
        </div>

        {state.paymentAmountsExpanded && (
          <div className="PaymentPageCustomization-sectionBody">
            <div className="PaymentPageCustomization-presetAmountsArea">
              {editorAmounts.map((amount, index) => (
                <div className="PaymentPageCustomization-presetAmountRow" key={index}>
                  <span className="PaymentPageCustomization-presetAmountLabel">
                    {textObj.presetAmountLabel} {index + 1}
                  </span>
                  <input
                    type="number"
                    min="1"
                    className="PaymentPageCustomization-presetAmountInput"
                    value={amount}
                    onChange={(e) => handlePresetAmountChange(index, e)}
                  />
                  {editorAmounts.length > MIN_PRESET_AMOUNTS && (
                    <div
                      className="PaymentPageCustomization-removeAmountBtn"
                      onClick={() => handleRemovePresetAmount(index)}
                      title={textObj.removeAmountLabel}
                    >
                      &times;
                    </div>
                  )}
                </div>
              ))}
              {editorAmounts.length < MAX_PRESET_AMOUNTS && (
                <div className="PaymentPageCustomization-addAmountBtn" onClick={handleAddPresetAmount}>
                  {textObj.addAmountBtn}
                </div>
              )}
              <div className="PaymentPageCustomization-resetBtn" onClick={handleResetPresets}>
                {textObj.resetPresetsBtn}
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="PaymentPageCustomization-liveDemo">
        <div className="PaymentPageCustomization-previewHeading">
          {textObj.donationPreviewHeading}
        </div>
        <div className="PaymentPageCustomization-previewPanel">
          <div
            className="MissionDetails-header PaymentPageCustomization-clickableGraphic"
            style={headerStyle}
            onClick={() => toggleColorKey("headerColor")}
          >
            <div className="MissionDetails-logo">
              {logo ? (
                <img className="PaymentPageCustomization-previewLogoImg" src={logo} alt="" />
              ) : (
                <div className="PaymentPageCustomization-previewLogoPlaceholder" style={headerTextStyle}>
                  L O G O
                </div>
              )}
            </div>
          </div>

          <div className="PaymentPageCustomization-previewBody">
            <div
              className="PaymentPageCustomization-previewProgressWrap PaymentPageCustomization-clickableGraphic"
              onClick={() => toggleColorKey("progressBarColor")}
            >
              <div className="PaymentPageCustomization-previewProgressTrack">
                <div
                  className="PaymentPageCustomization-previewProgressFill"
                  style={{
                    background: "#" + state.organizationObj.progressBarColor,
                    width: "65%",
                  }}
                ></div>
              </div>
            </div>

            <div className="MissionDetails-sectionHeading">
              {textObj.chooseAmount}
            </div>
            <div className="MissionDetails-amountShortcuts">
              {donationPreviewAmounts.map((amount, index) => (
                <div
                  key={index}
                  className="MissionDetails-amountShortcutBtn PaymentPageCustomization-clickableGraphic"
                  style={
                    index === 0
                      ? { ...headerStyle, ...buttonTextStyle, border: "2px solid #" + resolved.headerColor }
                      : {}
                  }
                  onClick={() => startEditingPresetIndex(index)}
                >
                  {state.editingPresetAmountIndex === index ? (
                    <input
                      type="number"
                      min="1"
                      autoFocus
                      className="PaymentPageCustomization-inlineAmountInput"
                      value={amount}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => handlePresetAmountChange(index, e)}
                      onBlur={stopEditingPresetIndex}
                      onKeyDown={(e) => e.key === "Enter" && stopEditingPresetIndex()}
                    />
                  ) : (
                    <>
                      HK${(Number(amount) || 0).toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </>
                  )}
                </div>
              ))}
              <button
                type="button"
                className="MissionDetails-amountShortcutBtn MissionDetails-otherAmountBtn"
              >
                {textObj.otherAmount}
              </button>
            </div>

            <button
              type="button"
              className="PaymentPageCustomization-previewNextBtn"
              style={{ ...headerStyle, ...buttonTextStyle }}
            >
              {textObj.nextBtn}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
